/**
 * QA Tools Field Mappings
 *
 * This file contains configuration for normalizing JSON/XML paths
 * and assigning them to sections that match the Report Edit UI.
 *
 * IMPORTANT: This file is only used by QA Tools and does not affect
 * any other part of the application.
 */

/** Section order matching Report Edit UI */
export const SECTION_ORDER = [
  'Report info',
  'Selection criteria',
  'Report fields',
  'Sort fields',
  'Filter detail',
  'Report options',
  'Report schedule',
  'Email Section',
  'Customize email',
  'Other',
] as const;

export type SectionName = (typeof SECTION_ORDER)[number];

/** Field mapping entry for path normalization */
export interface FieldMapping {
  patterns: RegExp[];
  canonical: string;
  section: SectionName;
  displayName?: string;
}

/**
 * Field mappings for normalizing JSON/XML paths to canonical keys.
 * Order matters - first match wins. More specific patterns should come first.
 */
export const FIELD_MAPPINGS: FieldMapping[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // REPORT INFO SECTION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    patterns: [/^requestid$/i, /\.requestid$/i],
    canonical: 'info.requestId',
    section: 'Report info',
    displayName: 'Request ID',
  },
  {
    patterns: [/^systemid$/i, /\.systemid$/i],
    canonical: 'info.systemId',
    section: 'Report info',
    displayName: 'System ID',
  },
  {
    patterns: [/^model$/i, /\.model$/i],
    canonical: 'info.model',
    section: 'Report info',
    displayName: 'Model',
  },
  {
    patterns: [/^owner$/i, /\.owner$/i],
    canonical: 'info.owner',
    section: 'Report info',
    displayName: 'Owner',
  },
  {
    patterns: [/^requestname$/i, /\.requestname$/i],
    canonical: 'info.requestName',
    section: 'Report info',
    displayName: 'Request Name',
  },
  {
    patterns: [/^signature$/i, /\.signature$/i],
    canonical: 'info.signature',
    section: 'Report info',
    displayName: 'Signature',
  },
  {
    patterns: [/^template$/i, /\.template$/i],
    canonical: 'info.template',
    section: 'Report info',
    displayName: 'Template',
  },
  {
    patterns: [/^quickfilterid$/i, /\.quickfilterid$/i],
    canonical: 'info.quickFilterId',
    section: 'Report info',
    displayName: 'Quick Filter ID',
  },
  {
    patterns: [/outputs?\.output\.reportid/i, /outputs?\[\d+\]\.reportid/i],
    canonical: 'info.reportMasterId',
    section: 'Report info',
    displayName: 'Report Master ID',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SORT FIELDS SECTION (must come before generic option matching)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    patterns: [/sortbreak\.sequence\.\d+/i],
    canonical: 'sort.sequence',
    section: 'Sort fields',
    displayName: 'Sort/Break',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // REPORT FIELDS SECTION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    patterns: [/customfield\.sequence\.\d+/i],
    canonical: 'fields.customField',
    section: 'Report fields',
    displayName: 'Custom Field',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // REPORT SCHEDULE SECTION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    patterns: [/schedule\.datetorun/i],
    canonical: 'schedule.dateToRun',
    section: 'Report schedule',
    displayName: 'Date to Run',
  },
  {
    patterns: [/schedule\.frequency/i],
    canonical: 'schedule.frequency',
    section: 'Report schedule',
    displayName: 'Frequency',
  },
  {
    patterns: [/schedule\.priority/i],
    canonical: 'schedule.priority',
    section: 'Report schedule',
    displayName: 'Priority',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FILTER DETAIL SECTION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    patterns: [/^filters?\[?\d*\]?\./i, /^filters?\./i],
    canonical: 'filter',
    section: 'Filter detail',
    displayName: 'Filter',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // EMAIL RECIPIENTS SECTION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    patterns: [/notifications\.notification\./i, /notifications?\[?\d*\]?\./i],
    canonical: 'recipients.notification',
    section: 'Email Section',
    displayName: 'Notification',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CUSTOMIZE EMAIL SECTION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    patterns: [/customemail\.emailsubject/i],
    canonical: 'email.subject',
    section: 'Customize email',
    displayName: 'Email Subject',
  },
  {
    patterns: [/customemail\.emailbody/i],
    canonical: 'email.body',
    section: 'Customize email',
    displayName: 'Email Body',
  },
  {
    patterns: [/customemail\.emailclose/i],
    canonical: 'email.close',
    section: 'Customize email',
    displayName: 'Email Close',
  },
];

/**
 * Options that should be categorized as "Sort fields"
 */
export const SORT_OPTIONS = new Set([
  'sort_column',
  'sort_order',
  'break',
  'page_break',
]);

/**
 * Options that should be categorized as "Report fields"
 */
export const REPORT_FIELD_OPTIONS = new Set(['custom_field']);

/**
 * Options that should be categorized as "Report options"
 */
export const REPORT_OPTIONS_SET = new Set([
  'output_format',
  'output_format_pdf_only',
  'output_type',
  'client_logo',
  'client_logo_internal',
  'autofit_xls',
  'report_name',
  'account_name',
  'display_currency',
  'report_criteria',
  'schedule_details_select',
  'transaction_date_select',
  'as_of_date_select',
]);

/**
 * Single-value options that should use sequence in path (not value)
 * These options have one value per report, so we don't want separate rows for each value
 */
export const SINGLE_VALUE_OPTIONS = new Set([
  'output_format',
  'output_format_pdf_only',
  'output_type',
  'client_logo',
  'client_logo_internal',
  'autofit_xls',
  'report_name',
  'report_level',
  'account_name',
  'display_currency',
  'report_criteria',
  'schedule_details_select',
  'transaction_date_select',
  'transaction_from_date',
  'transaction_to_date',
  'as_of_date_select',
  'as_of_date',
  'payee_name',
  'cont_num',
  'include_voids',
  'gross_net_paid_type',
  'non_financial_delta',
  'non_financial_field',
  'fin_custom_field_1',
  'fin_custom_field_2',
  'fin_custom_field_3',
  'finance_operator_1',
  'finance_operator_2',
  'finance_operator_3',
  'finance_operator_begin_1',
  'finance_operator_begin_2',
  'finance_operator_begin_3',
  'finance_operator_end_1',
  'finance_operator_end_2',
  'finance_operator_end_3',
  'compared_with_as_of_date',
  'incurred_change_greater',
  'paid_change_greater',
  'repeat_detection',
  'lag_time_calc_date_1',
  'lag_time_calc_date_2',
  'lagtime_boundary_1',
  'lagtime_boundary_2',
  'lagtime_boundary_3',
  'lagtime_boundary_4',
  'lagtime_boundary_5',
  'lagtime_boundary_6',
  'lagtime_boundary_7',
  'lagtime_boundary_8',
  'loss_payment_type',
  'severity_payment_type',
  'allocation_amount_from',
  'allocation_amount_through',
  'payment_amount_from',
  'payment_amount_through',
  'payment_cleared_from',
  'payment_cleared_through',
  'payment_escheated_from',
  'payment_escheated_through',
]);

/**
 * Options that should be categorized as "Report info"
 */
export const REPORT_INFO_OPTIONS = new Set(['cont_num']);

/**
 * Prefix for schedule increment options
 */
export const SCHEDULE_OPTIONS_PREFIX = 'increment_';

/**
 * Get section for an option by its name
 */
export function getSectionForOption(optionName: string): SectionName {
  const lowerName = optionName.toLowerCase();

  if (REPORT_INFO_OPTIONS.has(lowerName)) {
    return 'Report info';
  }
  if (SORT_OPTIONS.has(lowerName)) {
    return 'Sort fields';
  }
  if (REPORT_FIELD_OPTIONS.has(lowerName)) {
    return 'Report fields';
  }
  if (REPORT_OPTIONS_SET.has(lowerName)) {
    return 'Report options';
  }
  if (lowerName.startsWith(SCHEDULE_OPTIONS_PREFIX)) {
    return 'Report schedule';
  }

  // Default: Selection criteria (most ERF control options go here)
  return 'Selection criteria';
}

/**
 * Get display name for an option
 */
export function getOptionDisplayName(optionName: string): string {
  // Convert SNAKE_CASE to Title Case
  return optionName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
